import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { TextField } from "../shared/FormField";
import { apiClient, getApiErrorMessage, getFieldErrors } from "../../lib/apiClient";

const EMPTY_FORM = { name: "", quantity: "1", status: 1 };

export function ItemFormDialog({ open, onOpenChange, item, onSaved }) {
  const isEdit = Boolean(item);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(item ? { name: item.name || "", quantity: item.quantity || "1", status: item.status ?? 1 } : EMPTY_FORM);
    setErrors({});
  }, [open, item]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    const payload = { ...form, status: Number(form.status) };
    try {
      if (isEdit) {
        await apiClient.put(`/items/${item.id}`, payload);
        toast.success("Item updated");
      } else {
        await apiClient.post("/items", payload);
        toast.success("Item created");
      }
      onSaved();
    } catch (error) {
      setErrors(getFieldErrors(error));
      toast.error(getApiErrorMessage(error, "Could not save item."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="item-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Item" : "New Item"}</DialogTitle>
          <DialogDescription>A single menu item that can be included in one or more plans (e.g. Roti, Rice, Dal).</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <TextField label="Name" required value={form.name} onChange={setField("name")} error={errors.name} testId="item-form-name" />
          <TextField
            label="Quantity" required value={form.quantity} onChange={setField("quantity")} error={errors.quantity}
            hint="Each item is a single unit — defaults to 1." testId="item-form-quantity"
          />
          <div className="space-y-1.5">
            <Label htmlFor="item-form-status">Status</Label>
            <Select value={String(form.status)} onValueChange={(v) => setForm((f) => ({ ...f, status: Number(v) }))}>
              <SelectTrigger id="item-form-status" data-testid="item-form-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Active</SelectItem>
                <SelectItem value="2">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} data-testid="item-form-cancel-button">Cancel</Button>
            <Button type="submit" variant="accent" disabled={saving} data-testid="item-form-submit-button">
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Item"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
